import type { IconName } from '@fortawesome/fontawesome-common-types';
import {
  Avatar,
  Chip,
  FormControlLabel,
  Grid2,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import { Tag } from '@components/Tag';

const messages = defineMessages({
  configured: {
    id: 'GLOBAL.configured',
    defaultMessage: 'Configured',
  },
  notConfigured: {
    id: 'GLOBAL.notConfigured',
    defaultMessage: 'Not Configured',
  },
  verified: {
    id: 'GLOBAL.verified',
    defaultMessage: 'Verified',
  },
  notVerified: {
    id: 'GLOBAL.notVerified',
    defaultMessage: 'Not Verified',
  },
  on: {
    id: 'GLOBAL.on',
    defaultMessage: 'ON',
  },
  off: {
    id: 'GLOBAL.off',
    defaultMessage: 'OFF',
  },
});

interface ConfigOptionListProps {
  items: {
    /** Unique identifier of item in array */
    key: string;
    /** Callback that fires when a list item is clicked */
    handleClick: React.MouseEventHandler<HTMLDivElement>;
    /** Callback that fires when the toggle is used */
    onToggleChange?: (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => void;
    /** Icon name of list item */
    iconName: IconName;
    /** Is the option configured? */
    isConfigured: boolean;
    /** Is the option's toggle enabled? */
    isEnabled?: boolean;
    /** Is the option currently selected by the user? */
    isSelected: boolean;
    /** Is the option verified by two-factor authentication? */
    isVerified?: boolean;
    /** Is the (Not) Configured tag shown within the option? */
    showConfigTag: boolean;
    /** Is the (Not) Verified tag shown within the option? */
    showVerificationTag?: boolean;
    /** Option title */
    title: string;
    /** Option subtitle */
    subtitle: React.ReactNode;
    /** Is this item disabled/read-only? */
    disabled?: boolean;
    /** Optional security level tag (e.g. "Most Secure", "Less Secure") */
    securityTag?: {
      label: string;
      variant: 'success' | 'error';
    };
    /** Optional content rendered below the item, inside the card border */
    extraContent?: React.ReactNode;
  }[];
}

function ConfigOptionList({ items }: ConfigOptionListProps) {
  const { formatMessage } = useIntl();

  return (
    <List>
      {items.map((item) => {
        const {
          disabled = false,
          extraContent,
          key,
          handleClick,
          iconName,
          isConfigured,
          isEnabled,
          isSelected,
          isVerified,
          onToggleChange,
          securityTag,
          showConfigTag,
          showVerificationTag,
          subtitle,
          title,
        } = item;
        return (
          <ListItem
            disableGutters
            key={key}
            secondaryAction={
              onToggleChange ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      disabled={disabled}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                        checked: boolean
                      ) => onToggleChange(event, checked)}
                    />
                  }
                  label={formatMessage(isEnabled ? messages.on : messages.off)}
                />
              ) : undefined
            }
            sx={{
              px: 0,
              border: ({ palette: { divider, primary } }) =>
                `1px solid ${isSelected ? primary.main : divider}`,
              borderRadius: '5px',
              overflow: 'hidden',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <ListItemButton
              disabled={disabled}
              onClick={handleClick}
              sx={{
                flexWrap: {
                  xs: 'wrap',
                  sm: 'nowrap',
                },
              }}
            >
              <ListItemAvatar>
                <Avatar
                  sx={{
                    backgroundColor: 'transparent',
                    border: (theme) =>
                      `1px solid ${
                        isSelected
                          ? theme.palette.primary.main
                          : theme.palette.grey.A700
                      }`,
                  }}
                >
                  <Icon
                    color={isSelected ? 'primary.main' : 'grey.A700'}
                    name={iconName}
                    size="lg"
                    type="far"
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Grid2
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px',
                    }}
                  >
                    <Typography
                      color={isSelected ? 'primary.main' : 'textPrimary'}
                      variant="subtitle2"
                    >
                      {title}
                    </Typography>
                    {showConfigTag && (
                      <Tag
                        color={isConfigured ? 'success.main' : 'grey.A700'}
                        label={formatMessage(
                          isConfigured
                            ? messages.configured
                            : messages.notConfigured
                        )}
                      />
                    )}
                    {showVerificationTag && (
                      <Tag
                        color={isVerified ? 'success.main' : 'error.main'}
                        label={formatMessage(
                          isVerified ? messages.verified : messages.notVerified
                        )}
                        variant="primary"
                      />
                    )}
                    {securityTag && (
                      <Chip
                        label={securityTag.label}
                        size="small"
                        sx={({ palette }) => ({
                          fontSize: 10,
                          fontWeight: 500,
                          height: 18,
                          px: '2px',
                          color: palette[securityTag.variant].main,
                          backgroundColor: palette[securityTag.variant].light,
                          border: `1px solid ${palette[securityTag.variant].main}`,
                          '& .MuiChip-label': {
                            px: '5px',
                          },
                        })}
                      />
                    )}
                  </Grid2>
                }
                secondary={subtitle}
              />
            </ListItemButton>
            {extraContent}
          </ListItem>
        );
      })}
    </List>
  );
}

export default ConfigOptionList;
